// Analytics Calculation Engine

import { journeyTracker } from './journeyTracker';
import { relationshipGraph } from '../data/relationshipGraph';
import { masterRegistryEntries } from '../data/masterRegistryEntries';
import type {
  AnalyticsData,
  PageVisitData,
  FrameworkConnectionStats,
  CapabilityGapAnalysis,
  UsagePattern,
  ExplorationPath,
  MetaInsight,
  TimelineEvent,
  CommunityTrend,
} from '../types/analytics';

class AnalyticsEngine {
  /**
   * Generate comprehensive analytics from journey data
   */
  generateAnalytics(): AnalyticsData {
    const journey = journeyTracker.getJourney();
    const visitedPages = journey.visitedPages || [];
    
    return {
      // Overview metrics
      totalVisits: this.calculateTotalVisits(visitedPages),
      uniquePagesVisited: visitedPages.length,
      totalTimeSpent: this.calculateTotalTimeSpent(visitedPages),
      averageSessionLength: this.calculateAverageSessionLength(visitedPages),
      
      // Page visit data
      pageVisits: this.generatePageVisitData(visitedPages),
      
      // Framework insights
      frameworkConnections: this.analyzeFrameworkConnections(),
      
      // Capability gaps
      capabilityGaps: this.analyzeCapabilityGaps(visitedPages),
      
      // Usage patterns
      usagePatterns: this.analyzeUsagePatterns(visitedPages),
      
      // Exploration paths
      explorationPaths: this.analyzeExplorationPaths(visitedPages),
      
      // Meta insights
      metaInsights: this.generateMetaInsights(journey),
      
      // Timeline
      timeline: this.generateTimeline(journey),
      
      // Community trends (simulated)
      communityTrends: this.generateCommunityTrends(),
      
      // Last updated
      lastUpdated: new Date().toISOString(),
    };
  }

  private calculateTotalVisits(visitedPages: any[]): number {
    return visitedPages.reduce((sum, page) => sum + page.visitCount, 0);
  }

  private calculateTotalTimeSpent(visitedPages: any[]): number {
    return visitedPages.reduce((sum, page) => sum + (page.timeSpent || 0), 0);
  }

  private calculateAverageSessionLength(visitedPages: any[]): number {
    if (visitedPages.length === 0) return 0;
    const totalTime = this.calculateTotalTimeSpent(visitedPages);
    return Math.round(totalTime / visitedPages.length);
  }

  private generatePageVisitData(visitedPages: any[]): PageVisitData[] {
    return visitedPages.map(page => ({
      path: page.path,
      category: page.category,
      visitCount: page.visitCount,
      totalTimeSpent: page.timeSpent || 0,
      lastVisited: page.lastVisit,
      frameworkId: page.frameworkId,
    }));
  }

  private analyzeFrameworkConnections(): FrameworkConnectionStats[] {
    const stats: FrameworkConnectionStats[] = [];
    
    Object.entries(relationshipGraph).forEach(([frameworkId, connections]) => {
      const framework = masterRegistryEntries.find(e => e.encodedId === frameworkId);
      if (!framework) return;
      
      const inbound = connections.relatedTo.length;
      const outbound = connections.relatedFrom.length;
      const total = inbound + outbound;
      
      // Calculate centrality score (simple version: total connections)
      const centralityScore = total;
      
      stats.push({
        frameworkId,
        frameworkName: framework.canonicalName,
        connectionCount: total,
        inboundConnections: inbound,
        outboundConnections: outbound,
        centralityScore,
      });
    });
    
    // Sort by connection count
    return stats.sort((a, b) => b.connectionCount - a.connectionCount);
  }

  private analyzeCapabilityGaps(visitedPages: any[]): CapabilityGapAnalysis[] {
    const gaps: CapabilityGapAnalysis[] = [];
    
    // Group frameworks by MOF level
    const levelGroups = {
      'Engines': masterRegistryEntries.filter(e => e.sectionCode === 'p2p'),
      'M3: Metadata': masterRegistryEntries.filter(e => e.sectionCode === 'ms'),
      'M2: Models': masterRegistryEntries.filter(e => 
        ['pf', 'gf', 'cont'].includes(e.sectionCode)
      ),
    };
    
    Object.entries(levelGroups).forEach(([level, frameworks]) => {
      const visitedIds = visitedPages
        .filter(p => p.frameworkId)
        .map(p => p.frameworkId);
      
      const visitedInLevel = frameworks.filter(f => 
        visitedIds.includes(f.encodedId)
      );
      
      const unvisited = frameworks
        .filter(f => !visitedIds.includes(f.encodedId))
        .map(f => f.canonicalName);
      
      const coverage = frameworks.length > 0 
        ? (visitedInLevel.length / frameworks.length) * 100 
        : 0;
      
      gaps.push({
        mofLevel: level,
        visitedCount: visitedInLevel.length,
        totalCount: frameworks.length,
        coveragePercentage: Math.round(coverage),
        unvisitedFrameworks: unvisited.slice(0, 5), // Top 5 unvisited
      });
    });
    
    return gaps;
  }

  private analyzeUsagePatterns(visitedPages: any[]): UsagePattern[] {
    const patterns: Map<string, UsagePattern> = new Map();
    
    visitedPages.forEach(page => {
      const date = new Date(page.lastVisit);
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
      const hourOfDay = date.getHours();
      const key = `${dayOfWeek}-${hourOfDay}`;
      
      const existing = patterns.get(key);
      if (existing) {
        existing.visitCount += page.visitCount;
      } else {
        patterns.set(key, {
          dayOfWeek,
          hourOfDay,
          visitCount: page.visitCount,
        });
      }
    });
    
    return Array.from(patterns.values());
  }

  private analyzeExplorationPaths(visitedPages: any[]): ExplorationPath[] {
    // Simple implementation: most common sequences of 3 pages
    const paths: Map<string, ExplorationPath> = new Map();
    
    const sortedPages = [...visitedPages].sort((a, b) => 
      new Date(a.lastVisit).getTime() - new Date(b.lastVisit).getTime()
    );
    
    for (let i = 0; i < sortedPages.length - 2; i++) {
      const sequence = [
        sortedPages[i].category,
        sortedPages[i + 1].category,
        sortedPages[i + 2].category,
      ];
      const key = sequence.join(' → ');
      
      const existing = paths.get(key);
      if (existing) {
        existing.frequency++;
      } else {
        paths.set(key, {
          sequence,
          frequency: 1,
          avgSessionLength: 180, // Simulated
        });
      }
    }
    
    return Array.from(paths.values())
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 5);
  }

  private generateMetaInsights(journey: any): MetaInsight[] {
    const insights: MetaInsight[] = [];
    const visitedPages = journey.visitedPages || [];
    const achievements = journey.achievements || [];
    const stats = this.analyzeFrameworkConnections();
    
    // Most connected framework
    if (stats.length > 0) {
      const topFramework = stats[0];
      insights.push({
        type: 'connection',
        title: 'Most Connected Framework',
        description: `${topFramework.frameworkName} has the highest number of connections (${topFramework.connectionCount})`,
        metric: topFramework.connectionCount,
        trend: 'stable',
        actionable: `Explore ${topFramework.frameworkName} to understand system architecture`,
      });
    }
    
    // Coverage insight
    const gaps = this.analyzeCapabilityGaps(visitedPages);
    const avgCoverage = gaps.length > 0 
      ? gaps.reduce((sum, g) => sum + g.coveragePercentage, 0) / gaps.length 
      : 0;
    insights.push({
      type: 'coverage',
      title: 'Knowledge Coverage',
      description: `You've explored ${Math.round(avgCoverage)}% of the knowledge system`,
      metric: Math.round(avgCoverage),
      trend: avgCoverage > 50 ? 'up' : 'stable',
      actionable: avgCoverage < 80 ? 'Continue exploring to unlock more insights' : 'Great coverage!',
    });
    
    // Exploration trend
    const recentVisits = visitedPages.filter((p: any) => {
      const daysSince = (Date.now() - new Date(p.lastVisit).getTime()) / (1000 * 60 * 60 * 24);
      return daysSince < 7;
    }).length;
    
    insights.push({
      type: 'trend',
      title: 'Recent Activity',
      description: `${recentVisits} pages visited in the last 7 days`,
      metric: recentVisits,
      trend: recentVisits > 10 ? 'up' : 'stable',
    });
    
    // Achievement insight
    insights.push({
      type: 'pattern',
      title: 'Learning Achievements',
      description: `You've earned ${achievements.length} achievements`,
      metric: achievements.length,
      trend: 'up',
      actionable: 'Keep exploring to unlock more achievements',
    });
    
    return insights;
  }

  private generateTimeline(journey: any): TimelineEvent[] {
    const events: TimelineEvent[] = [];
    const visitedPages = journey.visitedPages || [];
    const achievements = journey.achievements || [];
    
    // First visit to system
    if (visitedPages.length > 0) {
      const firstVisit = visitedPages.reduce((earliest: any, page: any) => 
        new Date(page.firstVisit) < new Date(earliest.firstVisit) ? page : earliest
      );
      
      events.push({
        date: firstVisit.firstVisit,
        type: 'first-visit',
        title: 'Journey Began',
        description: 'Started exploring Peak Priorities Base Camp',
      });
    }
    
    // Achievement milestones
    achievements.forEach((achievement: any) => {
      events.push({
        date: achievement.unlockedAt,
        type: 'achievement',
        title: `Unlocked: ${achievement.name}`,
        description: achievement.description,
      });
    });
    
    // Framework mastery (5+ visits to same framework)
    const frameworkVisits = new Map<string, any>();
    visitedPages.forEach((page: any) => {
      if (page.frameworkId) {
        const existing = frameworkVisits.get(page.frameworkId);
        if (existing) {
          existing.count += page.visitCount;
        } else {
          frameworkVisits.set(page.frameworkId, {
            count: page.visitCount,
            lastVisit: page.lastVisit,
          });
        }
      }
    });
    
    frameworkVisits.forEach((data, frameworkId) => {
      if (data.count >= 5) {
        const framework = masterRegistryEntries.find(e => e.encodedId === frameworkId);
        if (framework) {
          events.push({
            date: data.lastVisit,
            type: 'framework-mastery',
            title: 'Framework Mastery',
            description: `Deep dive into ${framework.canonicalName}`,
            frameworkId,
          });
        }
      }
    });
    
    // Sort by date (newest first)
    return events.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    ).slice(0, 10);
  }

  private generateCommunityTrends(): CommunityTrend[] {
    // Simulated community trends
    const topFrameworks = [
      'pf.degap.stages',
      'gf.diagram.levels',
      'cont.multires.levels',
      'pf.framework.diagnostic.role.types',
      'gf.relevance.framework',
    ];
    
    return topFrameworks.map((id, index) => {
      const framework = masterRegistryEntries.find(e => e.encodedId === id);
      if (!framework) return null;
      
      return {
        frameworkId: id,
        frameworkName: framework.canonicalName,
        popularityScore: 100 - (index * 15),
        weeklyGrowth: Math.round(Math.random() * 20 - 5), // -5% to +15%
        topTags: framework.tags?.slice(0, 3) || [],
      };
    }).filter(Boolean) as CommunityTrend[];
  }

  /**
   * Get heat map data for usage patterns
   */
  getHeatMapData(): { day: string; hour: number; value: number }[] {
    const journey = journeyTracker.getJourney();
    const visitedPages = journey.visitedPages || [];
    const patterns = this.analyzeUsagePatterns(visitedPages);
    
    // Create full week grid
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const grid: { day: string; hour: number; value: number }[] = [];
    
    days.forEach(day => {
      hours.forEach(hour => {
        const pattern = patterns.find(p => p.dayOfWeek === day && p.hourOfDay === hour);
        grid.push({
          day,
          hour,
          value: pattern?.visitCount || 0,
        });
      });
    });
    
    return grid;
  }
}

export const analyticsEngine = new AnalyticsEngine();

